import { noticeDeadline } from '../../data/contracts';
import { addDays, daysUntil, parseIsoDate } from '../../data/dates';
import type { Contract, ContractId } from '../../data/types';

export type RenewalEventKind = 'notice_deadline' | 'auto_renews' | 'expires';

export interface RenewalEvent {
  /** `${contractId}:${kind}` */
  id: string;
  contractId: ContractId;
  kind: RenewalEventKind;
  date: Date;
  /** Calendar days from today. */
  daysAway: number;
}

export const RENEWAL_KIND_LABEL: Record<RenewalEventKind, string> = {
  notice_deadline: 'Notice deadline',
  auto_renews: 'Auto-renews',
  expires: 'Expires',
};

export type RenewalHorizon = 90 | 180 | 365;

export const RENEWAL_HORIZONS: { value: RenewalHorizon; label: string }[] = [
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '12 months' },
];

function eventsForContract(contract: Contract): RenewalEvent[] {
  if (contract.status === 'expired') return [];
  const events: RenewalEvent[] = [];
  const deadline = noticeDeadline(contract);
  if (deadline) {
    events.push({
      id: `${contract.id}:notice_deadline`,
      contractId: contract.id,
      kind: 'notice_deadline',
      date: deadline,
      daysAway: daysUntil(deadline),
    });
  }
  const end = parseIsoDate(contract.endDate);
  const kind: RenewalEventKind = contract.autoRenew.renews
    ? 'auto_renews'
    : 'expires';
  events.push({
    id: `${contract.id}:${kind}`,
    contractId: contract.id,
    kind,
    // An auto-renewal takes effect the day after the term ends.
    date: kind === 'auto_renews' ? addDays(end, 1) : end,
    daysAway: daysUntil(kind === 'auto_renews' ? addDays(end, 1) : end),
  });
  return events;
}

/**
 * Notice deadlines, auto-renewals and expiries that fall between today and
 * `horizonDays` from now, soonest first.
 */
export function buildRenewalEvents(
  contracts: Contract[],
  horizonDays: number,
): RenewalEvent[] {
  const annualValue = new Map(
    contracts.map((contract) => [contract.id, contract.annualValue]),
  );
  return contracts
    .flatMap(eventsForContract)
    .filter((event) => event.daysAway >= 0 && event.daysAway <= horizonDays)
    .sort(
      (a, b) =>
        a.date.getTime() - b.date.getTime() ||
        (annualValue.get(b.contractId) ?? 0) -
          (annualValue.get(a.contractId) ?? 0),
    );
}

/** Notice deadlines that fall within the next `days` days. */
export function countNoticeDeadlines(
  contracts: Contract[],
  days: number,
): number {
  return buildRenewalEvents(contracts, days).filter(
    (event) => event.kind === 'notice_deadline',
  ).length;
}

/** The notice deadline, renewal or expiry of one agreement, whenever it falls. */
export function renewalEventFor(
  contract: Contract,
  kind: RenewalEventKind,
): RenewalEvent | undefined {
  return buildRenewalEvents([contract], 100_000).find(
    (event) => event.kind === kind,
  );
}
